import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { formatDate } from '../../utils/timeFormat';
import { Progress } from '../../components/ui/progress';
import {
  ArrowLeft, Shield, Lock, User, Calendar, Briefcase, Link2, Globe,
  CheckCircle, CheckCircle2, AlertCircle, Loader2, Save, Plus, Trash2, Eye, EyeOff, MapPin,
  TrendingUp, Award, Star, Clock, Flame, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';
import { downscaleImage } from '../../utils/imageDownscale';
import { ETHNICITY_OPTIONS } from '../../lib/ethnicity';
import { handleSaveError, saveDraft, clearDraft, draftScope } from '../../utils/saveErrorHandler';
import AddressAutocomplete from '../../components/AddressAutocomplete';
import ResumePanel from '../../components/ResumePanel';
import { ReliabilityBadge, CommitmentBadge } from '../../components/ReliabilityBadge';
import ContactInfoPanel from '../../components/ContactInfoPanel';
import TalentMarketsCard from '../../components/TalentMarketsCard';
import RejectionBanner from '../../components/RejectionBanner';  // Iter-220p
import ProfileBoostInlineBanner from '../../components/ProfileBoostInlineBanner';  // Iter-221 — $4.99 boost visibility placement
import ProfileCompletenessNudge from '../../components/ProfileCompletenessNudge';  // Iter-291 — private "Complete your profile" reminder
import TalentBannerEditorPanel from '../../components/TalentBannerEditorPanel';  // Iter-290b Sprint B (#1) — customizable profile banner
import TalentCertificationsWidget from '../../components/TalentCertificationsWidget';  // moved off Home — lives under Training
import CreatorNetworkBanner from '../../components/CreatorNetworkBanner';  // moved off Home — Creator/Influencer management under Social

const API = process.env.REACT_APP_BACKEND_URL;
function getHeaders() { return { Authorization: `Bearer ${localStorage.getItem('token')}` }; }

// ── SSN Panel ───────────────────────────────────────────────
function SSNPanel() {
  const [status, setStatus] = useState(null);
  const [ssn, setSsn] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/profile-info/ssn/status');
        setStatus(res.data);
      } catch {}
    })();
  }, []);

  const formatSSN = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  };

  const handleSubmit = async () => {
    if (ssn.replace(/\D/g, '').length !== 9) return toast.error('SSN must be 9 digits');
    setSubmitting(true);
    try {
      const res = await api.post('/profile-info/ssn', { ssn });
      setStatus({ submitted: true, ssn_masked: res.data.ssn_masked });
      setShowInput(false);
      setSsn('');
      toast.success('SSN submitted securely');
    } catch (err) {
      // Iter-213+ P0 — surface the real backend error + correlation id.
      // We deliberately do NOT keep a localStorage draft of the raw SSN
      // (PII security) — the user re-enters if it failed.
      const info = handleSaveError(err, { endpoint: '/profile-info/ssn' });
      toast.error(info.message);
    } finally { setSubmitting(false); }
  };

  return (
    <Card data-testid="ssn-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-green-500" /> Social Security Number</CardTitle>
        <CardDescription className="text-xs">Encrypted at rest. Only last 4 digits visible.</CardDescription>
      </CardHeader>
      <CardContent>
        {status?.submitted && !showInput ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-green-500" />
              <span className="font-mono text-sm text-gray-700">{status.ssn_masked}</span>
              <Badge className="bg-green-100 text-green-700 text-xs">Submitted</Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setShowInput(true)} className="text-xs text-gray-500" data-testid="update-ssn-btn">Update</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={ssn}
                onChange={e => setSsn(formatSSN(e.target.value))}
                placeholder="XXX-XX-XXXX"
                maxLength={11}
                className="font-mono"
                data-testid="ssn-input"
              />
              <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="submit-ssn-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              </Button>
            </div>
            {showInput && <button onClick={() => setShowInput(false)} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>}
            <p className="text-[11px] text-gray-400">Your SSN is encrypted and only accessible to authorized personnel.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Required Fields Panel ───────────────────────────────────
function RequiredFieldsPanel() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/profile-info/required-fields-status');
      setData(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    // Refresh whenever any panel (photo, gig roles, basic info) announces
    // a successful save. This closes the "complete in one panel, banner
    // stale in another" gap the user flagged.
    const onUpdate = () => load();
    window.addEventListener('talent-profile-updated', onUpdate);
    return () => window.removeEventListener('talent-profile-updated', onUpdate);
  }, [load]);

  if (!data) return null;

  return (
    <Card className={data.complete ? 'border-green-200 bg-green-50/30' : 'border-amber-200 bg-amber-50/30'} data-testid="required-fields-panel">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {data.complete ? <CheckCircle className="w-5 h-5 text-green-500" /> : <AlertCircle className="w-5 h-5 text-amber-500" />}
          <span className="font-medium text-sm">{data.complete ? 'Profile Complete' : `${data.missing_fields.length} required field${data.missing_fields.length > 1 ? 's' : ''} missing`}</span>
        </div>
        {!data.complete && (
          <div className="flex flex-wrap gap-1.5 mt-1">
            {data.missing_fields.map(f => <Badge key={f} className="bg-amber-100 text-amber-700 text-xs">{f}</Badge>)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Gig Roles Panel ─────────────────────────────────────────
// Prominent entry into the full Gig Roles editor so talent can pick
// Influencer / Brand Ambassador / etc. right from their profile page.
// The user explicitly flagged that there was no visible way to select
// gig roles from the profile — this closes that gap without duplicating
// the editor UI (the multi-select lives at /talent/gig-roles).
function GigRolesPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/talent/profile/gig-roles');
      setRoles(res.data.gig_roles || []);
      setCustomRoles(res.data.custom_roles || []);
    } catch {
      setRoles([]); setCustomRoles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const onUpdate = () => load();
    window.addEventListener('talent-profile-updated', onUpdate);
    return () => window.removeEventListener('talent-profile-updated', onUpdate);
  }, [load]);

  const selectedCount = (roles?.length || 0) + (customRoles?.length || 0);
  const hasAny = selectedCount > 0;

  return (
    <Card
      className={hasAny ? 'border-pink-200' : 'border-amber-300 bg-amber-50/40 ring-2 ring-amber-100'}
      data-testid="gig-roles-panel"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-pink-500" /> Gig Roles
          {hasAny ? (
            <Badge className="bg-pink-100 text-pink-700 text-xs ml-auto">{selectedCount} selected</Badge>
          ) : (
            <Badge className="bg-amber-200 text-amber-800 text-xs ml-auto">Required</Badge>
          )}
        </CardTitle>
        <CardDescription className="text-xs">
          What kinds of gigs should you be matched for? (Brand Ambassador, Influencer, Bartender, Server, Event Staff, etc.)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : hasAny ? (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {roles.map((r, i) => (
              <Badge key={`r-${i}`} className="bg-pink-100 text-pink-700 text-xs">
                {r.role || r}
              </Badge>
            ))}
            {customRoles.map((r, i) => (
              <Badge key={`c-${i}`} className="bg-indigo-100 text-indigo-700 text-xs">
                {r.role || r} <span className="opacity-60 ml-1">(custom)</span>
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-xs text-amber-800 mb-3">
            You haven't selected any gig roles yet. Without this, you won't appear in client searches for Brand Ambassadors, Influencers, Bartenders, and other specialties.
          </p>
        )}
        <Button
          onClick={() => navigate('/talent/gig-roles')}
          className={hasAny ? 'bg-pink-500 hover:bg-pink-600 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'}
          data-testid="open-gig-roles-editor-btn"
        >
          <Sparkles className="w-4 h-4 mr-1.5" />
          {hasAny ? 'Edit Gig Roles' : 'Select Your Gig Roles'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Basic Info Panel ────────────────────────────────────────
function BasicInfoPanel() {
  const [nickname, setNickname] = useState('');
  const [middleName, setMiddleName] = useState('');
  const [ethnicity, setEthnicity] = useState('');
  const [dob, setDob] = useState('');
  const [bio, setBio] = useState('');
  const [minimumRate, setMinimumRate] = useState('');
  // Iter-411x — Available for Travel opt-in.
  const [availableForTravel, setAvailableForTravel] = useState(false);
  const [initial, setInitial] = useState({ nickname: '', middleName: '', ethnicity: '', dob: '', bio: '', minimumRate: '', availableForTravel: false });
  const [saving, setSaving] = useState(false);
  // Iter-281 P0 — Scope drafts to the LOADED profile so admin impersonation
  // or multi-account browsers cannot bleed draft data across profiles.
  const [profileEntityId, setProfileEntityId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/profile-info/full');
        const loaded = {
          nickname: res.data.nickname || '',
          middleName: res.data.middle_name || '',
          ethnicity: res.data.ethnicity || '',
          dob: res.data.date_of_birth || '',
          bio: res.data.bio || '',
          minimumRate: res.data.minimum_rate != null ? String(res.data.minimum_rate) : '',
          availableForTravel: Boolean(res.data.available_for_travel),
        };
        const entityId = res.data.user_id || res.data.id || null;
        setProfileEntityId(entityId);
        // Iter-213+ P0 — If a previous save failed and we backed up a
        // draft, prefer the user's pending edits over the server copy
        // so they don't lose data after refresh/crash.
        // Iter-281 P0 — draftScope now scopes by the loaded profile's
        // user_id so admin impersonation / multi-account browsers don't
        // restore a draft from a DIFFERENT profile (CRITICAL bug fix).
        let merged = loaded;
        try {
          // eslint-disable-next-line global-require
          const { loadDraft, draftScope: ds } = require('../../utils/saveErrorHandler');
          const scope = ds({ section: 'profile.basic', entityId });
          const draft = loadDraft(scope);
          if (draft && typeof draft === 'object') {
            merged = { ...loaded, ...draft };
            toast.info('Restored your unsaved Basic Info changes');
          }
        } catch (_) { /* ignore */ }
        setNickname(merged.nickname);
        setMiddleName(merged.middleName);
        setEthnicity(merged.ethnicity);
        setDob(merged.dob);
        setBio(merged.bio);
        setMinimumRate(merged.minimumRate);
        setAvailableForTravel(Boolean(merged.availableForTravel));
        setInitial(loaded);
      } catch {}
    })();
  }, []);

  // Iter-213+ P0 — Continuously back up the in-flight payload so a crash
  // or refresh during a long form session doesn't wipe the user's edits.
  // Iter-281 P0 — keyed by loaded profile id, NOT a static scope.
  useEffect(() => {
    const hasContent = nickname || middleName || ethnicity || dob || bio || minimumRate;
    if (!hasContent || !profileEntityId) return;
    const t = setTimeout(() => {
      saveDraft(
        draftScope({ section: 'profile.basic', entityId: profileEntityId }),
        { nickname, middleName, ethnicity, dob, bio, minimumRate },
      );
    }, 600);
    return () => clearTimeout(t);
  }, [nickname, middleName, ethnicity, dob, bio, minimumRate, profileEntityId]);

  const isDirty = (
    nickname !== initial.nickname ||
    middleName !== initial.middleName ||
    ethnicity !== initial.ethnicity ||
    dob !== initial.dob ||
    bio !== initial.bio ||
    minimumRate !== initial.minimumRate ||
    Boolean(availableForTravel) !== Boolean(initial.availableForTravel)
  );

  const saveAll = async () => {
    setSaving(true);
    const tasks = [];
    const summary = [];
    try {
      if (nickname !== initial.nickname) {
        if (nickname.trim()) {
          tasks.push(api.patch('/profile-info/nickname', { nickname: nickname.trim() }));
          summary.push('nickname');
        }
      }
      if (middleName !== initial.middleName) {
        tasks.push(api.patch('/profile-info/middle-name', { middle_name: middleName.trim() || null }));
        summary.push('middle name');
      }
      if (ethnicity !== initial.ethnicity) {
        tasks.push(api.patch('/profile-info/ethnicity', { ethnicity: ethnicity.trim() || null }));
        summary.push('ethnicity');
      }
      if (dob !== initial.dob && dob) {
        tasks.push(api.patch('/profile-info/date-of-birth', { date_of_birth: dob }));
        summary.push('date of birth');
      }
      if (bio !== initial.bio) {
        tasks.push(api.patch('/profile-info/bio', { bio }));
        summary.push('bio');
      }
      if (minimumRate !== initial.minimumRate) {
        tasks.push(api.patch('/profile-info/minimum-rate', {
          minimum_rate: minimumRate ? parseFloat(minimumRate) : null,
        }));
        summary.push('minimum rate');
      }
      // Iter-411x — Available for Travel opt-in.
      if (Boolean(availableForTravel) !== Boolean(initial.availableForTravel)) {
        tasks.push(api.patch('/profile-info/available-for-travel', {
          available_for_travel: Boolean(availableForTravel),
        }));
        summary.push('travel availability');
      }
      if (tasks.length === 0) {
        toast.info('No changes to save');
        return;
      }
      await Promise.all(tasks);
      setInitial({ nickname, middleName, ethnicity, dob, bio, minimumRate, availableForTravel });
      clearDraft(draftScope({ section: 'profile.basic', entityId: profileEntityId }));
      toast.success(`Saved ${summary.join(', ')}`);
      window.dispatchEvent(new CustomEvent('talent-profile-updated'));
    } catch (err) {
      const info = handleSaveError(err, {
        scope: draftScope({ section: 'profile.basic', entityId: profileEntityId }),
        endpoint: '/profile-info/basic',
        payload: { nickname, middleName, ethnicity, dob, bio, minimumRate },
      });
      toast.error(info.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="basic-info-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><User className="w-4 h-4 text-cyan-500" /> Basic Info</CardTitle>
        <CardDescription className="text-xs">Edit any field below, then click Save Basic Info to update all at once.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Nickname / Preferred Name</Label>
            <Input value={nickname} onChange={e => setNickname(e.target.value)} placeholder="What should we call you?" data-testid="nickname-input" className="text-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Date of Birth <span className="text-red-400">*</span></Label>
            <Input type="date" value={dob} onChange={e => setDob(e.target.value)} data-testid="dob-input" className="text-sm" />
          </div>
        </div>
        {/* Iter-281 Phase B Part 3 — talent self-edit middle_name + ethnicity */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Middle Name</Label>
            <Input
              value={middleName}
              onChange={e => setMiddleName(e.target.value)}
              placeholder="Optional"
              data-testid="middle-name-input"
              className="text-sm"
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Ethnicity</Label>
            <select
              value={ethnicity}
              onChange={e => setEthnicity(e.target.value)}
              data-testid="ethnicity-select"
              className="w-full text-sm rounded-md border border-gray-200 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
            >
              <option value="">— Not specified —</option>
              {ETHNICITY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Minimum Hourly Rate ($)</Label>
            <Input type="number" min="0" max="500" step="0.50" value={minimumRate} onChange={e => setMinimumRate(e.target.value)} placeholder="e.g. 25.00" data-testid="minimum-rate-input" className="text-sm" />
            <p className="text-[10px] text-gray-400">You'll only receive notifications for gigs at or above this rate</p>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-gray-500">Bio</Label>
          <textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Tell us about yourself..." rows={3} data-testid="bio-input"
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
        </div>

        {/* Iter-411x — Available for Travel opt-in. Surfaces on the
            public Booking Page and Discovery card when enabled. */}
        <div className="flex items-start justify-between gap-3 rounded-md border border-gray-200 bg-white px-3 py-2.5">
          <div className="min-w-0">
            <Label className="text-sm text-gray-800 font-medium">Available for Travel</Label>
            <p className="text-[11px] text-gray-500 leading-snug mt-0.5">
              Let clients know you&apos;re open to gigs outside your primary
              markets. Shows on your public booking page and discovery card.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={Boolean(availableForTravel)}
              onChange={(e) => setAvailableForTravel(e.target.checked)}
              data-testid="available-for-travel-toggle"
            />
            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-cyan-500 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5" />
          </label>
        </div>
        <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
          <p className="text-[11px] text-gray-400">
            {isDirty ? 'Unsaved changes' : 'All changes saved'}
          </p>
          <Button
            onClick={saveAll}
            disabled={saving || !isDirty}
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            data-testid="save-basic-info-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save Basic Info
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Physical Attributes Panel (for Models/Brand Ambassadors/Influencers) ──
function PhysicalAttributesPanel() {
  const [shirtSize, setShirtSize] = useState('');
  const [shoeSize, setShoeSize] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  // NEW Casting attributes
  const [eyeColor, setEyeColor] = useState('');
  const [hairColor, setHairColor] = useState('');
  const [hairLength, setHairLength] = useState('');
  const [weightLbs, setWeightLbs] = useState('');
  const [dressSize, setDressSize] = useState('');
  // Language attributes
  const [isBilingual, setIsBilingual] = useState(false);
  const [languagesSpoken, setLanguagesSpoken] = useState([]);
  const [primaryLanguage, setPrimaryLanguage] = useState('');
  const [languageFluencies, setLanguageFluencies] = useState({});
  const [hasTattoos, setHasTattoos] = useState(false);
  const [hasVisibleTattoos, setHasVisibleTattoos] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [initial, setInitial] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/profile-info/full');
        // Backend returns physical sizing under `physical` key
        const physical = res.data.physical || {};
        const loadedShirt = physical.shirt_size || '';
        const loadedShoe = physical.shoe_size != null ? String(physical.shoe_size) : '';
        let loadedHF = '';
        let loadedHI = '';
        if (physical.height_inches) {
          loadedHF = String(Math.floor(physical.height_inches / 12));
          loadedHI = String(physical.height_inches % 12);
        }
        setShirtSize(loadedShirt);
        setShoeSize(loadedShoe);
        setHeightFeet(loadedHF);
        setHeightInches(loadedHI);

        // Load casting attributes (appearance + language + tattoo)
        let castingData = {};
        try {
          const castingRes = await api.get('/profile-info/casting-profile');
          castingData = castingRes.data || {};
        } catch {}
        const appearance = castingData.appearance || {};
        const language = castingData.language || {};
        const tattoos = castingData.tattoos || {};

        const loaded = {
          shirtSize: loadedShirt,
          shoeSize: loadedShoe,
          heightFeet: loadedHF,
          heightInches: loadedHI,
          eyeColor: appearance.eye_color || '',
          hairColor: appearance.hair_color || '',
          hairLength: appearance.hair_length || '',
          weightLbs: appearance.weight_lbs ? String(appearance.weight_lbs) : '',
          dressSize: appearance.dress_size || '',
          isBilingual: !!language.is_bilingual,
          languagesSpoken: language.languages_spoken || [],
          primaryLanguage: language.primary_language || '',
          languageFluencies: language.language_fluencies || {},
          hasTattoos: !!tattoos.has_tattoos,
          hasVisibleTattoos: !!tattoos.has_visible_tattoos,
        };
        setEyeColor(loaded.eyeColor);
        setHairColor(loaded.hairColor);
        setHairLength(loaded.hairLength);
        setWeightLbs(loaded.weightLbs);
        setDressSize(loaded.dressSize);
        setIsBilingual(loaded.isBilingual);
        setLanguagesSpoken(loaded.languagesSpoken);
        setPrimaryLanguage(loaded.primaryLanguage);
        setLanguageFluencies(loaded.languageFluencies);
        setHasTattoos(loaded.hasTattoos);
        setHasVisibleTattoos(loaded.hasVisibleTattoos);
        setInitial(loaded);
      } catch {}
    })();
  }, []);

  const current = {
    shirtSize, shoeSize, heightFeet, heightInches,
    eyeColor, hairColor, hairLength, weightLbs, dressSize,
    isBilingual, languagesSpoken, primaryLanguage, languageFluencies,
    hasTattoos, hasVisibleTattoos,
  };
  const isDirty = initial && JSON.stringify(current) !== JSON.stringify(initial);

  const saveAll = async () => {
    setSaving(true);
    try {
      const totalInches = heightFeet && heightInches !== ''
        ? (parseInt(heightFeet) * 12 + parseInt(heightInches))
        : null;

      const tasks = [
        api.patch('/profile-info/physical-attributes', {
          shirt_size: shirtSize || null,
          shoe_size: shoeSize ? parseFloat(shoeSize) : null,
          height_inches: totalInches,
        }),
        api.patch('/profile-info/appearance-attributes', {
          eye_color: eyeColor || null,
          hair_color: hairColor || null,
          hair_length: hairLength || null,
          weight_lbs: weightLbs ? parseInt(weightLbs) : null,
          dress_size: dressSize || null,
        }),
        api.patch('/profile-info/language-attributes', {
          is_bilingual: isBilingual,
          languages_spoken: languagesSpoken,
          primary_language: primaryLanguage || null,
          language_fluencies: languageFluencies,
        }),
        api.patch('/profile-info/tattoo-attributes', {
          has_tattoos: hasTattoos,
          has_visible_tattoos: hasVisibleTattoos,
        }),
      ];
      await Promise.all(tasks);
      setInitial(current);
      toast.success('Casting attributes saved');
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 2200);
      window.dispatchEvent(new CustomEvent('talent-profile-updated'));
    } catch (err) {
      const info = handleSaveError(err, {
        scope: 'profile.casting',
        endpoint: '/profile-info/casting-attributes',
      });
      toast.error(info.message);
    } finally {
      setSaving(false);
    }
  };

  const shirtSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];
  const shoeSizes = ['5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13', '14', '15'];
  const eyeColors = ['blue', 'green', 'brown', 'hazel', 'gray', 'amber', 'other'];
  const hairColors = ['black', 'brown', 'blonde', 'red', 'auburn', 'gray', 'white', 'other'];
  const hairLengths = ['bald', 'buzz', 'short', 'medium', 'long', 'very_long'];
  const dressSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '0', '2', '4', '6', '8', '10', '12', '14', '16', '18', '20'];
  const allLanguages = ['English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi', 'Russian', 'Vietnamese', 'Tagalog', 'Polish', 'Haitian Creole', 'Hebrew', 'Greek', 'Dutch', 'ASL', 'Other'];

  const toggleLanguage = (lang) => {
    if (languagesSpoken.includes(lang)) {
      setLanguagesSpoken(languagesSpoken.filter(l => l !== lang));
      const newFluencies = { ...languageFluencies };
      delete newFluencies[lang];
      setLanguageFluencies(newFluencies);
      if (primaryLanguage === lang) setPrimaryLanguage('');
    } else {
      setLanguagesSpoken([...languagesSpoken, lang]);
      setLanguageFluencies({ ...languageFluencies, [lang]: 'conversational' });
    }
  };

  const setFluency = (lang, level) => {
    setLanguageFluencies({ ...languageFluencies, [lang]: level });
  };

  const fluencyLabels = { basic: 'Basic', conversational: 'Conversational', fluent: 'Fluent' };
  const fluencyColors = { basic: 'bg-gray-100 text-gray-700', conversational: 'bg-cyan-100 text-cyan-700', fluent: 'bg-green-100 text-green-700' };

  return (
    <Card data-testid="physical-attributes-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <User className="w-4 h-4 text-pink-500" /> Casting Attributes
        </CardTitle>
        <CardDescription className="text-xs">Edit any field below, then click Save Casting Attributes to update them all.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Basic Sizing */}
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Sizing</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Shirt Size</Label>
              <select value={shirtSize} onChange={e => setShirtSize(e.target.value)} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" data-testid="shirt-size-select">
                <option value="">Select...</option>
                {shirtSizes.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Shoe Size (US)</Label>
              <select value={shoeSize} onChange={e => setShoeSize(e.target.value)} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" data-testid="shoe-size-select">
                <option value="">Select...</option>
                {shoeSizes.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label className="text-xs text-gray-500">Height</Label>
              <div className="flex gap-1 items-center min-w-0">
                <select value={heightFeet} onChange={e => setHeightFeet(e.target.value)} className="flex-1 min-w-0 rounded-md border border-gray-200 px-2 py-2 text-sm" data-testid="height-feet-select">
                  <option value="">ft</option>
                  {[4, 5, 6, 7].map(f => <option key={f} value={f}>{f}'</option>)}
                </select>
                <select value={heightInches} onChange={e => setHeightInches(e.target.value)} className="flex-1 min-w-0 rounded-md border border-gray-200 px-2 py-2 text-sm" data-testid="height-inches-select">
                  <option value="">in</option>
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => <option key={i} value={i}>{i}"</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Appearance Attributes */}
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Appearance</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Eye Color</Label>
              <select value={eyeColor} onChange={e => setEyeColor(e.target.value)} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm capitalize" data-testid="eye-color-select">
                <option value="">Select...</option>
                {eyeColors.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Hair Color</Label>
              <select value={hairColor} onChange={e => setHairColor(e.target.value)} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm capitalize" data-testid="hair-color-select">
                <option value="">Select...</option>
                {hairColors.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Hair Length</Label>
              <select value={hairLength} onChange={e => setHairLength(e.target.value)} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm capitalize" data-testid="hair-length-select">
                <option value="">Select...</option>
                {hairLengths.map(l => <option key={l} value={l}>{l.replace('_', ' ')}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Weight (lbs)</Label>
              <Input type="number" value={weightLbs} onChange={e => setWeightLbs(e.target.value)} placeholder="e.g. 150" className="text-sm" data-testid="weight-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">Dress Size</Label>
              <select value={dressSize} onChange={e => setDressSize(e.target.value)} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm" data-testid="dress-size-select">
                <option value="">Select...</option>
                {dressSizes.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Language Attributes */}
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Languages</h4>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Primary Language (Native)</Label>
                <select 
                  value={primaryLanguage} 
                  onChange={e => setPrimaryLanguage(e.target.value)} 
                  className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  data-testid="primary-language-select"
                >
                  <option value="">Select your primary language...</option>
                  {allLanguages.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={isBilingual} onChange={e => setIsBilingual(e.target.checked)} className="rounded border-gray-300" data-testid="bilingual-checkbox" />
                  <span className="text-gray-700">I am bilingual / multilingual</span>
                </label>
              </div>
            </div>
            
            <div>
              <Label className="text-xs text-gray-500 mb-2 block">Additional Languages (select all that apply)</Label>
              <div className="flex flex-wrap gap-1.5">
                {allLanguages.map(lang => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => toggleLanguage(lang)}
                    className={`px-2 py-1 rounded text-xs border transition-colors ${languagesSpoken.includes(lang) ? 'bg-cyan-100 border-cyan-300 text-cyan-700' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`}
                    data-testid={`lang-${lang.toLowerCase().replace(' ', '-')}`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Fluency Levels for selected languages */}
            {languagesSpoken.length > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <Label className="text-xs text-gray-500 mb-2 block">Set fluency level for each language</Label>
                <div className="space-y-2">
                  {languagesSpoken.map(lang => (
                    <div key={lang} className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        {lang}
                        {lang === primaryLanguage && <span className="ml-1 text-xs text-cyan-600">(Primary)</span>}
                      </span>
                      <div className="flex gap-1">
                        {['basic', 'conversational', 'fluent'].map(level => (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setFluency(lang, level)}
                            className={`px-2 py-0.5 rounded text-xs border transition-colors ${(languageFluencies[lang] || 'conversational') === level ? fluencyColors[level] + ' border-current' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                            data-testid={`fluency-${lang.toLowerCase()}-${level}`}
                          >
                            {fluencyLabels[level]}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tattoo Attributes */}
        <div>
          <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Tattoos</h4>
          <div className="flex items-center gap-6 flex-wrap">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={hasTattoos} onChange={e => { setHasTattoos(e.target.checked); if (!e.target.checked) setHasVisibleTattoos(false); }} className="rounded border-gray-300" data-testid="tattoos-checkbox" />
              <span className="text-gray-700">I have tattoos</span>
            </label>
            <label className={`flex items-center gap-2 text-sm ${hasTattoos ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
              <input type="checkbox" checked={hasVisibleTattoos} onChange={e => setHasVisibleTattoos(e.target.checked)} disabled={!hasTattoos} className="rounded border-gray-300" data-testid="visible-tattoos-checkbox" />
              <span className="text-gray-700">Visible when wearing short sleeves</span>
            </label>
          </div>
        </div>

        {/* Single consolidated Save button for the whole section */}
        <div className="pt-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <p
            className={`text-xs font-medium ${
              !initial ? 'text-gray-400' : isDirty ? 'text-amber-600' : justSaved ? 'text-emerald-600' : 'text-gray-400'
            }`}
            data-testid="casting-attributes-status"
          >
            {!initial
              ? 'Loading…'
              : isDirty
                ? '● Unsaved changes'
                : justSaved
                  ? '✓ Saved'
                  : 'All changes saved'}
          </p>
          <Button
            onClick={saveAll}
            disabled={saving}
            className={`min-w-[200px] transition-colors ${
              justSaved
                ? 'bg-emerald-500 hover:bg-emerald-500 text-white'
                : isDirty
                  ? 'bg-pink-500 hover:bg-pink-600 text-white shadow-md ring-2 ring-pink-200'
                  : 'bg-pink-500/90 hover:bg-pink-600 text-white'
            }`}
            data-testid="save-casting-attributes-btn"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Saving…</>
            ) : justSaved ? (
              <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Saved</>
            ) : (
              <><Save className="w-4 h-4 mr-1.5" /> Save Casting Attributes</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Location Panel ──────────────────────────────────────────
function LocationPanel() {
  const [fullAddress, setFullAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [unitApt, setUnitApt] = useState('');
  const [additionalAddress, setAdditionalAddress] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [coords, setCoords] = useState({ lat: null, lng: null });
  const [saving, setSaving] = useState(false);
  const [addressError, setAddressError] = useState('');
  // Iter-281 P0 — Scope drafts to the loaded profile (admin-impersonation safety).
  const [profileEntityId, setProfileEntityId] = useState(null);
  // Iter-213+ P0 — manual-entry escape hatch. When Google autocomplete
  // fails (quota / denied / mobile z-index / Samsung Internet) the user
  // MUST still be able to complete onboarding. Toggle exposes city/state/
  // zip/country as plain inputs and removes the coords hard-requirement.
  const [manualMode, setManualMode] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/profile-info/full');
        const loc = res.data.location || {};
        setProfileEntityId(res.data.user_id || res.data.id || null);
        setFullAddress(loc.full_address || '');
        setUnitApt(loc.unit_apt || '');
        setAdditionalAddress(loc.additional_address || '');
        setCity(loc.city || '');
        setState(loc.state || '');
        setZipCode(loc.zip_code || '');
        setCountry(loc.country || '');
        setCountryCode(loc.country_code || '');
        if (loc.lat && loc.lng) {
          setCoords({ lat: loc.lat, lng: loc.lng });
        }
      } catch {}
    })();
  }, []);

  const handleAddressSelect = (data) => {
    setAddressError('');
    if (data) {
      setFullAddress(data.address || '');
      setCity(data.city || '');
      setState(data.state_code || data.state || '');
      setZipCode(data.postal_code || '');
      setCountry(data.country || '');
      setCountryCode(data.country_code || '');
      setCoords({ lat: data.lat, lng: data.lng });
    } else {
      setFullAddress('');
      setCoords({ lat: null, lng: null });
    }
  };

  const saveLocation = async () => {
    // Iter-213+ P0 — onboarding cannot be blocked by missing autocomplete
    // coordinates. We require ONLY a non-empty street address. If coords
    // are missing we'll geocode async on the backend; if city/state/zip
    // are missing in manual mode we surface a soft warning but still save.
    if (!fullAddress || !fullAddress.trim()) {
      setAddressError('Please enter your street address.');
      return;
    }
    if (manualMode && (!city.trim() || !state.trim())) {
      setAddressError('Please enter at least a city and state.');
      return;
    }
    setAddressError('');
    setSaving(true);
    const locationPayload = {
      full_address: fullAddress,
      unit_apt: unitApt || null,
      additional_address: additionalAddress || null,
      city: city || null,
      state: state || null,
      zip_code: zipCode || null,
      country: country || null,
      country_code: countryCode || null,
      // coords are now optional. Backend geocodes async when null.
      location_lat: coords.lat,
      location_lng: coords.lng,
      // Tell the backend this row needs a deferred geocode pass.
      manual_entry: manualMode || (!coords.lat || !coords.lng),
    };
    try {
      saveDraft(draftScope({ section: 'profile.location', entityId: profileEntityId }), locationPayload);
      await api.patch('/profile-info/location', locationPayload);
      clearDraft(draftScope({ section: 'profile.location', entityId: profileEntityId }));
      toast.success(
        (!coords.lat || !coords.lng)
          ? 'Address saved — we\'ll verify coordinates shortly.'
          : 'Address saved successfully'
      );
    } catch (err) {
      const info = handleSaveError(err, {
        scope: draftScope({ section: 'profile.location', entityId: profileEntityId }),
        endpoint: '/profile-info/location',
        payload: locationPayload,
      });
      toast.error(info.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="location-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <MapPin className="w-4 h-4 text-emerald-500" /> Full Address
        </CardTitle>
        <CardDescription className="text-xs">
          Enter your complete address for accurate gig matching
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Full Address Input - Required. Iter-213+ P0 — when manualMode is
            on we render plain Inputs instead of the Google autocomplete so
            users can't get trapped if Places API is unavailable. */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs text-gray-700 font-medium">
              Street Address <span className="text-red-500">*</span>
            </Label>
            <button
              type="button"
              onClick={() => setManualMode(m => !m)}
              className="text-[11px] text-cyan-600 hover:text-cyan-700 underline underline-offset-2 shrink-0"
              data-testid="location-toggle-manual"
            >
              {manualMode ? 'Use address suggestions' : "Can't find your address? Enter manually"}
            </button>
          </div>
          {manualMode ? (
            <Input
              value={fullAddress}
              onChange={e => { setFullAddress(e.target.value); setAddressError(''); }}
              placeholder="e.g. 1234 Peachtree St NE"
              className="text-sm"
              required
              data-testid="location-address-manual-input"
            />
          ) : (
            <AddressAutocomplete
              value={fullAddress}
              onChange={setFullAddress}
              onSelect={handleAddressSelect}
              placeholder="Enter your full street address..."
              required
              error={addressError}
              onApiUnavailable={() => setManualMode(true)}
              data-testid="location-address-input"
            />
          )}
          <p className="text-[10px] text-gray-400">
            {manualMode
              ? 'Enter your address as you would write it on mail. We\'ll verify coordinates after you save.'
              : 'Select from suggestions to capture accurate coordinates — or use manual entry if your address isn\'t listed.'}
          </p>
          {addressError && manualMode && (
            <p className="text-xs text-red-500" data-testid="location-error">{addressError}</p>
          )}
        </div>

        {/* Unit/Apt and Additional Address */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Unit / Apt #</Label>
            <Input
              value={unitApt}
              onChange={e => setUnitApt(e.target.value)}
              placeholder="Unit, Apt, Suite..."
              className="text-sm"
              data-testid="location-unit"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-gray-500">Additional Info</Label>
            <Input
              value={additionalAddress}
              onChange={e => setAdditionalAddress(e.target.value)}
              placeholder="Building name, gate code..."
              className="text-sm"
              data-testid="location-additional"
            />
          </div>
        </div>

        {/* City / State / ZIP / Country — Iter-213+ P0 — when manualMode is
            on these are PLAIN INPUTS the user fills directly. When using
            autocomplete they show the auto-filled values read-only. */}
        {manualMode ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label className="text-xs text-gray-500">City <span className="text-red-500">*</span></Label>
              <Input value={city} onChange={e => setCity(e.target.value)} placeholder="Atlanta" className="text-sm" data-testid="location-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">State <span className="text-red-500">*</span></Label>
              <Input value={state} onChange={e => setState(e.target.value)} placeholder="GA" maxLength={20} className="text-sm" data-testid="location-state" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-gray-500">ZIP</Label>
              <Input value={zipCode} onChange={e => setZipCode(e.target.value)} placeholder="30301" className="text-sm" data-testid="location-zip" />
            </div>
            <div className="space-y-1.5 col-span-2 sm:col-span-1">
              <Label className="text-xs text-gray-500">Country</Label>
              <Input value={country} onChange={e => setCountry(e.target.value)} placeholder="USA" className="text-sm" data-testid="location-country" />
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Auto-filled from address</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 min-w-0">
              <div className="min-w-0">
                <p className="text-[10px] text-gray-400">City</p>
                <p className="text-xs text-gray-700 truncate">{city || '—'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-gray-400">State</p>
                <p className="text-xs text-gray-700 truncate">{state || '—'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-gray-400">ZIP</p>
                <p className="text-xs text-gray-700 truncate">{zipCode || '—'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-gray-400">Country</p>
                <p className="text-xs text-gray-700 truncate">{country || 'USA'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Coordinates captured indicator. Iter-213+ — softened from a
            blocker to an informational chip. Missing coords no longer
            prevents save; backend geocodes async. */}
        {coords.lat && coords.lng ? (
          <div className="flex items-center gap-2 text-xs text-emerald-600 bg-emerald-50 px-3 py-2 rounded-lg">
            <CheckCircle className="w-3.5 h-3.5" />
            Location coordinates captured ({coords.lat.toFixed(4)}, {coords.lng.toFixed(4)})
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
            <AlertCircle className="w-3.5 h-3.5 text-amber-500" />
            We'll verify coordinates after saving — you can continue onboarding.
          </div>
        )}

        <Button
          onClick={saveLocation}
          disabled={saving || !fullAddress}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="save-location"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Save Location
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Work Experience Panel ───────────────────────────────────
function WorkExperiencePanel() {
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [profileEntityId, setProfileEntityId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/profile-info/full');
        setProfileEntityId(res.data.user_id || res.data.id || null);
        const exp = res.data.work_experience;
        setEntries(Array.isArray(exp) ? exp : []);
      } catch {}
    })();
  }, []);

  const addEntry = () => setEntries([...entries, { title: '', company: '', start_date: '', end_date: '', description: '' }]);
  const removeEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));
  const updateEntry = (i, field, val) => setEntries(entries.map((e, idx) => idx === i ? { ...e, [field]: val } : e));

  const saveAll = async () => {
    setSaving(true);
    const wxScope = draftScope({ section: 'profile.work_experience', entityId: profileEntityId });
    try {
      saveDraft(wxScope, entries);
      await api.put('/profile-info/work-experience', { work_experience: entries });
      clearDraft(wxScope);
      toast.success('Work experience saved');
    } catch (err) {
      const info = handleSaveError(err, {
        scope: wxScope,
        endpoint: '/profile-info/work-experience',
        payload: entries,
      });
      toast.error(info.message);
    }
    finally { setSaving(false); }
  };

  return (
    <Card data-testid="work-experience-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2"><Briefcase className="w-4 h-4 text-indigo-500" /> Work Experience</CardTitle>
          <Button size="sm" variant="ghost" onClick={addEntry} data-testid="add-experience"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {entries.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No work experience added yet</p>}
        {entries.map((e, i) => (
          <div key={i} className="border rounded-lg p-3 space-y-2 relative" data-testid={`experience-entry-${i}`}>
            <button onClick={() => removeEntry(i)} className="absolute top-2 right-2 text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Job Title" value={e.title} onChange={ev => updateEntry(i, 'title', ev.target.value)} className="text-sm" />
              <Input placeholder="Company" value={e.company} onChange={ev => updateEntry(i, 'company', ev.target.value)} className="text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Start (YYYY-MM)" value={e.start_date} onChange={ev => updateEntry(i, 'start_date', ev.target.value)} className="text-sm" />
              <Input placeholder="End (YYYY-MM or Present)" value={e.end_date} onChange={ev => updateEntry(i, 'end_date', ev.target.value)} className="text-sm" />
            </div>
            <textarea placeholder="Description" value={e.description} onChange={ev => updateEntry(i, 'description', ev.target.value)} rows={2}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500" />
          </div>
        ))}
        {entries.length > 0 && (
          <Button onClick={saveAll} disabled={saving} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="save-experience">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save All
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// ── Social Links Panel ──────────────────────────────────────
function SocialLinksPanel() {
  const [links, setLinks] = useState({});
  const [saving, setSaving] = useState(false);
  const [profileEntityId, setProfileEntityId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/profile-info/full');
        setProfileEntityId(res.data.user_id || res.data.id || null);
        const sl = res.data.social_links;
        setLinks(typeof sl === 'object' && sl ? sl : {});
      } catch {}
    })();
  }, []);

  const saveLinks = async () => {
    setSaving(true);
    const slScope = draftScope({ section: 'profile.social_links', entityId: profileEntityId });
    try {
      saveDraft(slScope, links);
      await api.patch('/profile-info/social-links', links);
      clearDraft(slScope);
      toast.success('Social links saved');
    } catch (err) {
      const info = handleSaveError(err, {
        scope: slScope,
        endpoint: '/profile-info/social-links',
        payload: links,
      });
      toast.error(info.message);
    }
    finally { setSaving(false); }
  };

  const fields = [
    { key: 'instagram', label: 'Instagram', placeholder: '@handle' },
    { key: 'tiktok', label: 'TikTok', placeholder: '@handle' },
    { key: 'linkedin', label: 'LinkedIn', placeholder: 'URL' },
    { key: 'twitter', label: 'Twitter/X', placeholder: '@handle' },
    { key: 'youtube', label: 'YouTube', placeholder: 'Channel URL' },
    { key: 'website', label: 'Website', placeholder: 'https://' },
  ];

  return (
    <Card data-testid="social-links-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Globe className="w-4 h-4 text-blue-500" /> Social Links</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          {fields.map(f => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs text-gray-500">{f.label}</Label>
              <Input value={links[f.key] || ''} onChange={e => setLinks({ ...links, [f.key]: e.target.value })} placeholder={f.placeholder} className="text-sm" data-testid={`social-${f.key}`} />
            </div>
          ))}
        </div>
        <Button onClick={saveLinks} disabled={saving} variant="outline" className="w-full" data-testid="save-social-links">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Save Links
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Certifications & Training Panel ──────────────────────────
function CertificationsPanel() {
  const navigate = useNavigate();
  const [certifications, setCertifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/training/certifications');
        setCertifications(res.data.platform_certifications || []);
      } catch {}
      
      try {
        const hubRes = await api.get('/training/hub');
        setStats(hubRes.data.stats);
      } catch {}
      
      setLoading(false);
    })();
  }, []);

  return (
    <Card data-testid="certifications-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-purple-500" /> Certifications & Training
          </CardTitle>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => navigate('/talent/training')}
            className="text-purple-600 border-purple-200 hover:bg-purple-50"
          >
            <Briefcase className="w-3 h-3 mr-1" /> Training Hub
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-purple-500" />
          </div>
        ) : certifications.length > 0 ? (
          <>
            {/* Progress Stats */}
            {stats && (
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-purple-700">Training Progress</span>
                  <span className="font-semibold text-purple-900">{stats.completion_rate}% Complete</span>
                </div>
                <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all" 
                    style={{ width: `${stats.completion_rate}%` }}
                  />
                </div>
                <p className="text-xs text-purple-600 mt-1">
                  {stats.total_certifications} of {stats.total_modules_available} certifications earned
                </p>
              </div>
            )}
            
            {/* Certification Badges */}
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Your Certifications</Label>
              <div className="flex flex-wrap gap-2">
                {certifications.map(cert => (
                  <Badge 
                    key={cert.id} 
                    className="bg-gradient-to-r from-purple-100 to-indigo-100 text-purple-800 border-purple-200 px-3 py-1.5"
                  >
                    <CheckCircle className="w-3.5 h-3.5 mr-1.5 text-green-600" />
                    {cert.badge_name}
                    {cert.is_verified && (
                      <Shield className="w-3 h-3 ml-1.5 text-purple-600" title="Verified" />
                    )}
                  </Badge>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <Shield className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500 mb-3">No certifications yet</p>
            <p className="text-xs text-gray-400 mb-3">
              Complete training modules to earn certifications and unlock premium shifts
            </p>
            <Button 
              onClick={() => navigate('/talent/training')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Start Training
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Availability Panel ──────────────────────────────────────
function AvailabilityPanel() {
  const [entries, setEntries] = useState([]);
  const [newDate, setNewDate] = useState('');
  const [newStatus, setNewStatus] = useState('available');
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);

  const month = new Date().toISOString().slice(0, 7);

  const fetchAvailability = useCallback(async () => {
    try {
      const res = await api.get(`/profile-info/availability?month=${month}`);
      setEntries(res.data);
    } catch {}
  }, [month]);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  const addEntry = async () => {
    if (!newDate) return toast.error('Select a date');
    setSaving(true);
    try {
      await api.post('/profile-info/availability', { entries: [{ date: newDate, status: newStatus, note: newNote }] });
      setNewDate(''); setNewNote('');
      fetchAvailability();
      toast.success('Availability updated');
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const removeEntry = async (date) => {
    try {
      await api.delete(`/profile-info/availability/${date}`);
      fetchAvailability();
      toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  const statusColors = { available: 'bg-green-100 text-green-700', unavailable: 'bg-red-100 text-red-700', tentative: 'bg-amber-100 text-amber-700' };

  return (
    <Card data-testid="availability-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2"><Calendar className="w-4 h-4 text-teal-500" /> Availability ({month})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="text-sm" data-testid="avail-date" />
          <select value={newStatus} onChange={e => setNewStatus(e.target.value)} className="border rounded-md px-2 py-1 text-sm" data-testid="avail-status">
            <option value="available">Available</option>
            <option value="unavailable">Unavailable</option>
            <option value="tentative">Tentative</option>
          </select>
          <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Note..." className="text-sm flex-1" data-testid="avail-note" />
          <Button onClick={addEntry} disabled={saving} size="sm" className="bg-teal-500 hover:bg-teal-600 text-white" data-testid="add-avail">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
        {entries.length === 0 ? <p className="text-xs text-gray-400 text-center py-3">No availability set for this month</p> :
          <div className="space-y-1">
            {entries.map(e => (
              <div key={e.date} className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-50" data-testid={`avail-entry-${e.date}`}>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">{formatDate(e.date)}</span>
                  <Badge className={`text-xs ${statusColors[e.status]}`}>{e.status}</Badge>
                  {e.note && <span className="text-xs text-gray-400">{e.note}</span>}
                </div>
                <button onClick={() => removeEntry(e.date)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        }
      </CardContent>
    </Card>
  );
}

// ── Performance & Reliability Panel ───────────────────────────────────────────────
function PerformancePanel() {
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState(null);

  useEffect(() => {
    const fetchScores = async () => {
      try {
        const res = await api.get('/reliability/my-scores');
        setScores(res.data);
      } catch (err) {
        console.error('Failed to fetch scores:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchScores();
  }, []);

  if (loading) {
    return (
      <Card data-testid="performance-panel">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan-500" />
          <span className="ml-2 text-gray-500">Loading performance data...</span>
        </CardContent>
      </Card>
    );
  }

  const reliability = scores?.reliability || {};
  const commitment = scores?.commitment || {};
  const isNewTalent = reliability.badge === 'new_talent' || reliability.total_shifts_completed < 5;

  const ScoreStat = ({ label, value, icon: Icon, color = 'text-cyan-500' }) => (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      <span className="font-semibold text-gray-900">{value}</span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="performance-panel">
      {/* Reliability Score Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-500" />
              Performance & Reliability
            </CardTitle>
            {!isNewTalent && (
              <ReliabilityBadge 
                badge={reliability.badge} 
                score={reliability.score} 
                showScore={true}
                size="sm"
              />
            )}
          </div>
          {isNewTalent && (
            <CardDescription className="mt-2 text-amber-600 bg-amber-50 rounded-md p-2 text-xs">
              <strong>New to platform</strong> — Complete at least 5 shifts to build your reliability score. This helps clients trust you for future bookings.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {isNewTalent ? (
            <div className="text-center py-6">
              <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No performance data yet</p>
              <p className="text-gray-400 text-xs mt-1">Complete shifts to build your score</p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Main Reliability Score — Iter-234f labeled as AI-generated */}
              <div className="bg-gradient-to-r from-cyan-50 to-green-50 rounded-lg p-4 mb-4" data-testid="ai-reliability-card">
                <div className="flex items-center justify-between mb-2">
                  <span
                    className="text-sm font-medium text-gray-700 inline-flex items-center gap-1"
                    title="iEvntür AI computes your Reliability Score from 5 signals: on-time arrival %, no-show %, talent ratings, cancellation history, and shifts completed."
                  >
                    🤖 AI Reliability Score
                  </span>
                  <span className="text-2xl font-bold text-cyan-600">{reliability.score?.toFixed(0) || 0}%</span>
                </div>
                <Progress value={reliability.score || 0} className="h-2" />
                <p className="text-xs text-gray-500 mt-2">
                  AI analyzes your attendance, punctuality, completion rate, and cancellation history to compute this score.
                </p>
              </div>

              {/* Detailed Stats */}
              <ScoreStat 
                label="Attendance Rate" 
                value={`${reliability.attendance_rate?.toFixed(0) || 100}%`}
                icon={CheckCircle}
                color="text-green-500"
              />
              <ScoreStat 
                label="On-Time Rate" 
                value={`${reliability.on_time_rate?.toFixed(0) || 100}%`}
                icon={Clock}
                color="text-blue-500"
              />
              <ScoreStat 
                label="Completion Rate" 
                value={`${reliability.completion_rate?.toFixed(0) || 100}%`}
                icon={Award}
                color="text-purple-500"
              />
              <ScoreStat 
                label="Shifts Completed" 
                value={reliability.total_shifts_completed || 0}
                icon={Star}
                color="text-amber-500"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Commitment Score Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              Multi-Day Commitment
            </CardTitle>
            {commitment.badge && commitment.badge !== 'new_talent' && (
              <CommitmentBadge 
                badge={commitment.badge} 
                completed={commitment.multiday_completed}
                size="sm"
              />
            )}
          </div>
        </CardHeader>
        <CardContent>
          {commitment.multiday_total < 3 ? (
            <div className="text-center py-4">
              <p className="text-gray-500 text-sm">Complete 3+ multi-day assignments to earn your commitment badge</p>
              <p className="text-gray-400 text-xs mt-1">
                Multi-day: {commitment.multiday_completed || 0} / {commitment.multiday_total || 0} completed
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Commitment Score</span>
                  <span className="text-2xl font-bold text-orange-600">{commitment.score?.toFixed(0) || 0}%</span>
                </div>
                <Progress value={commitment.score || 0} className="h-2" />
              </div>
              
              <ScoreStat 
                label="Multi-Day Assignments Completed" 
                value={commitment.multiday_completed || 0}
                icon={CheckCircle}
                color="text-green-500"
              />
              <ScoreStat 
                label="Total Multi-Day Assignments" 
                value={commitment.multiday_total || 0}
                icon={Calendar}
                color="text-blue-500"
              />
              {commitment.multiday_dropoffs > 0 && (
                <ScoreStat 
                  label="Drop-offs / Cancellations" 
                  value={commitment.multiday_dropoffs}
                  icon={AlertCircle}
                  color="text-red-500"
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tips Card */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-100">
        <CardContent className="py-4">
          <h4 className="font-semibold text-purple-900 text-sm mb-2">💡 Boost Your Score</h4>
          <ul className="text-xs text-purple-800 space-y-1">
            <li>• Show up on time for every shift</li>
            <li>• Complete all shifts you commit to</li>
            <li>• Avoid last-minute cancellations</li>
            <li>• Build trust with multi-day assignments</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────
// ── Profile Photo Panel ─────────────────────────────────────
function ProfilePhotoPanel() {
  const [currentUrl, setCurrentUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/talent/profile');
        setCurrentUrl(res.data?.profile_photo_url || '');
      } catch {}
    })();
  }, []);

  const handleFile = async (e) => {
    let file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    file = await downscaleImage(file); // mobile camera photos are 6-15MB raw
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }
    setUploading(true);
    try {
      // Step 1: Cloudinary signature from our backend
      const sigRes = await api.post('/cloudinary/signature', {
        folder: 'ieventur/profile-photos',
        resource_type: 'image',
      });
      const { signature, timestamp, cloud_name, api_key } = sigRes.data;

      // Step 2: Upload to Cloudinary (defensive: read body as text ONCE, then parse)
      const formData = new FormData();
      formData.append('file', file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', 'ieventur/profile-photos');

      const uploadRes = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`,
        { method: 'POST', body: formData, credentials: 'omit' }
      );

      // Read body ONCE (avoids "body stream already read" if anything else tries to consume it)
      const rawBody = await uploadRes.text();
      if (!uploadRes.ok) {
        let errMsg = `Cloudinary ${uploadRes.status}`;
        try {
          const parsed = JSON.parse(rawBody);
          errMsg = parsed?.error?.message || errMsg;
        } catch {
          errMsg = rawBody ? rawBody.slice(0, 180) : errMsg;
        }
        throw new Error(errMsg);
      }
      let uploadData;
      try {
        uploadData = JSON.parse(rawBody);
      } catch {
        throw new Error('Cloudinary returned invalid JSON');
      }
      if (!uploadData?.secure_url) {
        throw new Error('No secure_url in upload response');
      }

      // Step 3: Save URL to the backend
      await api.patch('/talent/profile/photo', {
        profile_photo_url: uploadData.secure_url,
      });

      setCurrentUrl(uploadData.secure_url);
      toast.success('Profile photo updated!');
      // Trigger listeners (OnboardingChecklist, RequiredFieldsPanel) to refresh
      window.dispatchEvent(new CustomEvent('talent-profile-updated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to upload photo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Card data-testid="profile-photo-panel">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <User className="w-4 h-4 text-cyan-600" />
          Profile Photo
        </CardTitle>
        <CardDescription>
          A clear, recent photo of yourself. Clients see this on your profile — it's required before you can be booked.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 rounded-full bg-gray-100 border-2 border-cyan-200 overflow-hidden flex items-center justify-center shrink-0">
            {currentUrl ? (
              <img src={currentUrl} alt="Profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
            ) : (
              <User className="w-10 h-10 text-gray-400" />
            )}
          </div>
          <div className="flex-1 space-y-2">
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="upload-profile-photo-btn"
            >
              {uploading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading…</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" />{currentUrl ? 'Replace Photo' : 'Upload Photo'}</>
              )}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
              data-testid="profile-photo-file-input"
            />
            <p className="text-xs text-gray-500">JPG or PNG, up to 10 MB. Well-lit, face clearly visible.</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


export default function ProfileInfo() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-clip" data-testid="profile-info-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-gray-500"><ArrowLeft className="w-5 h-5" /></Button>
          <User className="w-5 h-5 text-cyan-500" />
          <h1 className="text-base font-semibold">Profile Information</h1>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-5 py-5 sm:py-6 space-y-6">
        {/* Iter-220p — Always-on rejection banner so users who land on
            profile can find the re-upload CTA without bouncing between pages. */}
        <RejectionBanner />
        {/* Iter-291 — Private "Complete your profile" nudge. Public booking
            page never shows a completeness warning to clients; this is the
            talent-side reminder to add bio / photos / roles / certs. */}
        <ProfileCompletenessNudge />
        {/* Iter-221 — $4.99 Profile Boost visibility placement. Self-hides
            if the talent already has an active boost so we don't upsell
            something they already bought. */}
        <ProfileBoostInlineBanner source="profile_info" />
        {/* Contact Info card removed — the verified "Contact & Login" panel
            (ContactInfoPanel, under Info & Security tab) is now the single
            source of truth for login email + phone, with email/phone
            verification. Avoids a duplicate, unverified contact section. */}
        <RequiredFieldsPanel />

        <Tabs defaultValue="info" className="space-y-4 sm:space-y-6">
          <TabsList className="bg-white border w-full flex flex-wrap justify-start gap-1 gap-y-1.5 p-1.5 h-auto min-h-10 mb-4 sm:mb-5">
            <TabsTrigger value="info" className="flex-1 sm:flex-none text-xs sm:text-sm px-2.5 sm:px-3 whitespace-nowrap" data-testid="tab-info">Info &amp; Security</TabsTrigger>
            <TabsTrigger value="performance" className="flex-1 sm:flex-none text-xs sm:text-sm px-2.5 sm:px-3 whitespace-nowrap" data-testid="tab-performance">Performance</TabsTrigger>
            <TabsTrigger value="experience" className="flex-1 sm:flex-none text-xs sm:text-sm px-2.5 sm:px-3 whitespace-nowrap" data-testid="tab-experience">Experience</TabsTrigger>
            <TabsTrigger value="training" className="flex-1 sm:flex-none text-xs sm:text-sm px-2.5 sm:px-3 whitespace-nowrap" data-testid="tab-training">Training</TabsTrigger>
            <TabsTrigger value="social" className="flex-1 sm:flex-none text-xs sm:text-sm px-2.5 sm:px-3 whitespace-nowrap" data-testid="tab-social">Social</TabsTrigger>
            <TabsTrigger value="availability" className="flex-1 sm:flex-none text-xs sm:text-sm px-2.5 sm:px-3 whitespace-nowrap" data-testid="tab-availability">Availability</TabsTrigger>
            <TabsTrigger value="look" className="flex-1 sm:flex-none text-xs sm:text-sm px-2.5 sm:px-3 whitespace-nowrap" data-testid="tab-look">Look</TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="space-y-5 mt-4 sm:mt-5">
            <ProfilePhotoPanel />
            <ContactInfoPanel />
            <GigRolesPanel />
            <SSNPanel />
            <BasicInfoPanel />
            <LocationPanel />
            <TalentMarketsCard variant="talent" />
            <PhysicalAttributesPanel />
          </TabsContent>
          <TabsContent value="performance" className="mt-4 sm:mt-5"><PerformancePanel /></TabsContent>
          <TabsContent value="experience" className="mt-4 sm:mt-5 space-y-5">
            <ResumePanel />
            <WorkExperiencePanel />
          </TabsContent>
          <TabsContent value="training" className="mt-4 sm:mt-5 space-y-5">
            <TalentCertificationsWidget />
            <CertificationsPanel />
          </TabsContent>
          <TabsContent value="social" className="mt-4 sm:mt-5 space-y-5">
            <CreatorNetworkBanner />
            <SocialLinksPanel />
          </TabsContent>
          <TabsContent value="availability" className="mt-4 sm:mt-5"><AvailabilityPanel /></TabsContent>
          <TabsContent value="look" className="mt-4 sm:mt-5"><TalentBannerEditorPanel /></TabsContent>{/* Iter-290b Sprint B (#1) */}
        </Tabs>
      </div>
    </div>
  );
}
